import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {
    staffAddStudent,
    staffAddStudentsBulk,
    staffCreateClass,
    staffCreateSchool,
    staffCreateTeacher,
    staffGetClasses,
    staffGetDashboard,
    staffGetProjects,
    staffGetSchools,
    staffGetStudents,
    staffGetTeachers,
    staffPatchClass,
    staffRemoveStudent,
    staffResetCoordinatorPassword
} from '../../lib/labcode-staff-api';

import styles from './staff-portal.css';

const MAX_STUDENTS_BULK = 500;

/**
 * @param {string} text Conteúdo da área de texto (linhas e/ou vírgulas).
 * @returns {string[]} Apelidos únicos pela ordem de aparição.
 */
const parseNicknamesBulk = text => {
    if (!text || typeof text !== 'string') {
        return [];
    }
    const parts = text
        .split(/\r?\n/)
        .flatMap(line => line.split(/[,;]/).map(s => s.trim()))
        .filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const p of parts) {
        if (seen.has(p)) {
            continue;
        }
        seen.add(p);
        out.push(p);
    }
    return out;
};

const StaffDashboardView = ({ staffProfile }) => {
    const isOrgAdmin = staffProfile && staffProfile.role === 'org_admin';
    const isSchoolAdmin = staffProfile && staffProfile.role === 'school_admin';
    const showSchoolsManagement = isOrgAdmin || isSchoolAdmin;
    const [schools, setSchools] = useState([]);
    const [schoolsError, setSchoolsError] = useState(null);
    const [filterSchoolId, setFilterSchoolId] = useState('');
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [newSchoolName, setNewSchoolName] = useState('');
    const [coordEmail, setCoordEmail] = useState('');
    const [coordFullName, setCoordFullName] = useState('');
    const [coordPassword, setCoordPassword] = useState('');
    const [schoolFormError, setSchoolFormError] = useState(null);
    const [schoolCreatePending, setSchoolCreatePending] = useState(false);
    const [resetTarget, setResetTarget] = useState(null);
    const [resetPwd, setResetPwd] = useState('');
    const [resetPwd2, setResetPwd2] = useState('');
    const [resetError, setResetError] = useState(null);
    const [resetBusy, setResetBusy] = useState(false);

    const reloadSchools = useCallback(async () => {
        setSchoolsError(null);
        try {
            const { schools: s } = await staffGetSchools();
            setSchools(s || []);
        } catch (e) {
            setSchools([]);
            setSchoolsError(e && e.message ? e.message : 'Erro ao carregar escolas.');
        }
    }, []);

    useEffect(() => {
        if (!staffProfile || !showSchoolsManagement) return;
        reloadSchools();
    }, [staffProfile, showSchoolsManagement, reloadSchools]);

    useEffect(() => {
        if (!staffProfile) return;
        let cancelled = false;
        (async () => {
            setError(null);
            try {
                const d = await staffGetDashboard(
                    isOrgAdmin && filterSchoolId ? filterSchoolId : undefined
                );
                if (!cancelled) setStats(d);
            } catch (e) {
                if (!cancelled) {
                    setStats(null);
                    setError(e && e.message ? e.message : 'Erro ao carregar dashboard.');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [staffProfile, isOrgAdmin, filterSchoolId]);

    const handleCreateSchool = useCallback(async e => {
        e.preventDefault();
        setSchoolFormError(null);
        const name = newSchoolName.trim();
        if (!name) {
            setSchoolFormError('Indique o nome da escola.');
            return;
        }
        const email = coordEmail.trim().toLowerCase();
        const fullNm = coordFullName.trim();
        const pass = coordPassword;
        if (!email) {
            setSchoolFormError('Indique o email do coordenador.');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setSchoolFormError('Email do coordenador inválido.');
            return;
        }
        if (!fullNm) {
            setSchoolFormError('Indique o nome completo do coordenador.');
            return;
        }
        if (!pass || pass.length < 8) {
            setSchoolFormError('Palavra-passe do coordenador: mínimo 8 caracteres.');
            return;
        }
        setSchoolCreatePending(true);
        try {
            const data = await staffCreateSchool({
                name,
                coordinator: {
                    email,
                    fullName: fullNm,
                    password: pass
                }
            });
            const created = data.school;
            await reloadSchools();
            setNewSchoolName('');
            setCoordEmail('');
            setCoordFullName('');
            setCoordPassword('');
            if (created && created.id) {
                setFilterSchoolId(created.id);
            }
        } catch (err) {
            setSchoolFormError(
                err && err.message ? err.message : 'Erro ao criar escola.'
            );
        } finally {
            setSchoolCreatePending(false);
        }
    }, [newSchoolName, coordEmail, coordFullName, coordPassword, reloadSchools]);

    const openCoordinatorReset = useCallback(row => {
        if (!row.coordinator || !row.coordinator.email) {
            return;
        }
        setResetError(null);
        setResetPwd('');
        setResetPwd2('');
        setResetTarget({
            schoolId: row.id,
            schoolName: row.name,
            email: row.coordinator.email,
            fullName: row.coordinator.fullName
        });
    }, []);

    const closeCoordinatorReset = useCallback(() => {
        setResetTarget(null);
        setResetPwd('');
        setResetPwd2('');
        setResetError(null);
    }, []);

    const handleCoordinatorResetSubmit = useCallback(
        async e => {
            e.preventDefault();
            if (!resetTarget) return;
            setResetError(null);
            if (!resetPwd || resetPwd.length < 8) {
                setResetError('Nova palavra-passe: mínimo 8 caracteres.');
                return;
            }
            if (resetPwd !== resetPwd2) {
                setResetError('As palavras-passe não coincidem.');
                return;
            }
            setResetBusy(true);
            try {
                await staffResetCoordinatorPassword(resetTarget.schoolId, {
                    password: resetPwd
                });
                closeCoordinatorReset();
                await reloadSchools();
            } catch (err) {
                setResetError(
                    err && err.message ? err.message : 'Erro ao repor palavra-passe.'
                );
            } finally {
                setResetBusy(false);
            }
        },
        [resetTarget, resetPwd, resetPwd2, closeCoordinatorReset, reloadSchools]
    );

    return (
        <>
            <h2>{'Dashboard da rede'}</h2>
            {isOrgAdmin ? (
                <div className={styles.toolbar}>
                    <label htmlFor="staff-dash-school">{'Escola'}</label>
                    <select
                        className={styles.schoolSelect}
                        id="staff-dash-school"
                        value={filterSchoolId}
                        onChange={e => setFilterSchoolId(e.target.value)}
                    >
                        <option value="">{'Toda a rede'}</option>
                        {schools.map(s => (
                            <option
                                key={s.id}
                                value={s.id}
                            >
                                {s.name}
                            </option>
                        ))}
                    </select>
                </div>
            ) : null}
            {showSchoolsManagement ? (
                <>
                    <h3
                        className={styles.muted}
                        style={{marginTop: 0}}
                    >
                        {'Escolas e coordenadores'}
                    </h3>
                    <p className={styles.muted}>
                        {
                            'Lista das escolas no âmbito da sua rede. O coordenador entra no portal com o email indicado. ' +
                            'Escolas sem coordenador associado (por exemplo, escola padrão antiga) mostram traços até existir conta de coordenação.'
                        }
                    </p>
                    {schoolsError ? (
                        <p
                            className={styles.errorText}
                            role="alert"
                        >
                            {schoolsError}
                        </p>
                    ) : null}
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>{'Escola'}</th>
                                <th>{'Coordenador'}</th>
                                <th>{'Email'}</th>
                                <th>{'Acções'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {schools.length === 0 && !schoolsError ? (
                                <tr>
                                    <td colSpan={4}>
                                        {'Nenhuma escola no âmbito.'}
                                    </td>
                                </tr>
                            ) : (
                                schools.map(s => (
                                    <tr key={s.id}>
                                        <td>{s.name}</td>
                                        <td>
                                            {s.coordinator ? s.coordinator.fullName : '—'}
                                        </td>
                                        <td>
                                            {s.coordinator ? s.coordinator.email : '—'}
                                        </td>
                                        <td>
                                            <button
                                                className={styles.btnPrimary}
                                                disabled={!s.coordinator}
                                                type="button"
                                                onClick={() => openCoordinatorReset(s)}
                                            >
                                                {'Repor palavra-passe'}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {resetTarget ? (
                        <div className={styles.formCard}>
                            <h3
                                className={styles.muted}
                                style={{marginTop: 0}}
                            >
                                {'Nova palavra-passe do coordenador'}
                            </h3>
                            <p className={styles.muted}>
                                <strong>{resetTarget.schoolName}</strong>
                                {' · '}
                                {resetTarget.fullName}
                                {' · '}
                                {resetTarget.email}
                            </p>
                            {resetError ? (
                                <p
                                    className={styles.errorText}
                                    role="alert"
                                >
                                    {resetError}
                                </p>
                            ) : null}
                            <form onSubmit={handleCoordinatorResetSubmit}>
                                <div className={styles.formRow}>
                                    <input
                                        autoComplete="new-password"
                                        className={styles.textInput}
                                        maxLength={128}
                                        minLength={8}
                                        placeholder="Nova palavra-passe (mín. 8)"
                                        type="password"
                                        value={resetPwd}
                                        disabled={resetBusy}
                                        onChange={e => setResetPwd(e.target.value)}
                                    />
                                    <input
                                        autoComplete="new-password"
                                        className={styles.textInput}
                                        maxLength={128}
                                        minLength={8}
                                        placeholder="Confirmar palavra-passe"
                                        type="password"
                                        value={resetPwd2}
                                        disabled={resetBusy}
                                        onChange={e => setResetPwd2(e.target.value)}
                                    />
                                </div>
                                <div className={styles.formRow}>
                                    <button
                                        className={styles.btnPrimary}
                                        disabled={resetBusy}
                                        type="submit"
                                    >
                                        {resetBusy ? 'A guardar…' : 'Guardar nova palavra-passe'}
                                    </button>
                                    <button
                                        className={styles.btnNeutral}
                                        disabled={resetBusy}
                                        type="button"
                                        onClick={closeCoordinatorReset}
                                    >
                                        {'Cancelar'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : null}
                </>
            ) : null}
            {isOrgAdmin ? (
                <div className={styles.formCard}>
                    <h3
                        className={styles.muted}
                        style={{marginTop: 0}}
                    >
                        {'Nova escola'}
                    </h3>
                    <p className={styles.muted}>
                        {
                            'Cria a escola na sua organização e, na mesma operação, a conta de coordenação pedagógica dessa escola (pode entrar no portal com esse email).'
                        }
                    </p>
                    {schoolFormError ? (
                        <p
                            className={styles.errorText}
                            role="alert"
                        >
                            {schoolFormError}
                        </p>
                    ) : null}
                    <form onSubmit={handleCreateSchool}>
                        <p className={styles.muted}>
                            <strong>{'Escola'}</strong>
                        </p>
                        <div className={styles.formRow}>
                            <input
                                autoComplete="organization"
                                className={styles.textInput}
                                id="staff-new-school-name"
                                maxLength={255}
                                placeholder="Nome da escola"
                                type="text"
                                value={newSchoolName}
                                disabled={schoolCreatePending}
                                onChange={e => setNewSchoolName(e.target.value)}
                            />
                        </div>
                        <p className={styles.muted}>
                            <strong>{'Coordenador pedagógico (obrigatório)'}</strong>
                        </p>
                        <div className={styles.formRow}>
                            <input
                                autoComplete="email"
                                className={styles.textInput}
                                id="staff-new-school-coord-email"
                                maxLength={320}
                                placeholder="Email do coordenador"
                                type="email"
                                value={coordEmail}
                                disabled={schoolCreatePending}
                                onChange={e => setCoordEmail(e.target.value)}
                            />
                            <input
                                autoComplete="name"
                                className={styles.textInput}
                                id="staff-new-school-coord-name"
                                maxLength={255}
                                placeholder="Nome completo"
                                type="text"
                                value={coordFullName}
                                disabled={schoolCreatePending}
                                onChange={e => setCoordFullName(e.target.value)}
                            />
                        </div>
                        <div className={styles.formRow}>
                            <input
                                autoComplete="new-password"
                                className={styles.textInput}
                                id="staff-new-school-coord-pass"
                                maxLength={128}
                                minLength={8}
                                placeholder="Palavra-passe (mín. 8 caracteres)"
                                type="password"
                                value={coordPassword}
                                disabled={schoolCreatePending}
                                onChange={e => setCoordPassword(e.target.value)}
                            />
                            <button
                                className={styles.btnPrimary}
                                disabled={schoolCreatePending}
                                type="submit"
                            >
                                {schoolCreatePending ? 'A criar…' : 'Criar escola e coordenador'}
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}
            {error ? (
                <p
                    className={styles.errorText}
                    role="alert"
                >
                    {error}
                </p>
            ) : null}
            {stats ? (
                <div className={styles.cardGrid}>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>
                            {stats.schoolsCount}
                        </div>
                        <div className={styles.statLabel}>
                            {'Escolas no âmbito'}
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>
                            {stats.classesCount}
                        </div>
                        <div className={styles.statLabel}>
                            {'Turmas'}
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>
                            {stats.studentsCount}
                        </div>
                        <div className={styles.statLabel}>
                            {'Alunos (apelidos)'}
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>
                            {stats.projectsCount}
                        </div>
                        <div className={styles.statLabel}>
                            {'Projetos guardados'}
                        </div>
                    </div>
                </div>
            ) : (
                !error ? (
                    <p className={styles.muted}>
                        {'A carregar…'}
                    </p>
                ) : null
            )}
            <div className={styles.hintBox}>
                <p>
                    {
                        'Resumo para secretaria ou escola: contagens respeitam o seu papel e o filtro de escola. ' +
                        'Professores e coordenação vêem apenas a própria escola.'
                    }
                </p>
            </div>
        </>
    );
};

StaffDashboardView.propTypes = {
    staffProfile: PropTypes.object
};

StaffDashboardView.defaultProps = {
    staffProfile: null
};

const StaffClassesView = ({ staffProfile }) => {
    const isOrgAdmin = staffProfile && staffProfile.role === 'org_admin';
    const isTeacher = staffProfile && staffProfile.role === 'teacher';
    const canAssignClassTeacher =
        staffProfile &&
        ['org_admin', 'school_admin', 'coordinator'].includes(staffProfile.role);
    const [schools, setSchools] = useState([]);
    const [filterSchoolId, setFilterSchoolId] = useState('');
    const [classes, setClasses] = useState([]);
    const [selectedClassId, setSelectedClassId] = useState('');
    const [students, setStudents] = useState([]);
    const [newCode, setNewCode] = useState('');
    const [newName, setNewName] = useState('');
    const [newClassTeacherId, setNewClassTeacherId] = useState('');
    const [teachersList, setTeachersList] = useState([]);
    const [busyClassId, setBusyClassId] = useState('');
    const [newNick, setNewNick] = useState('');
    const [bulkText, setBulkText] = useState('');
    const [bulkSummary, setBulkSummary] = useState(null);
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(false);

    const schoolQuery = isOrgAdmin && filterSchoolId ? filterSchoolId : undefined;

    const teachersForNewClass = useMemo(() => {
        if (isOrgAdmin) {
            if (!filterSchoolId) {
                return [];
            }
            return teachersList.filter(t => t.schoolId === filterSchoolId);
        }
        return teachersList;
    }, [isOrgAdmin, filterSchoolId, teachersList]);

    const teachersQueryParam =
        isOrgAdmin && filterSchoolId ? filterSchoolId : undefined;

    const reloadClasses = useCallback(async () => {
        const { classes: c } = await staffGetClasses(schoolQuery);
        setClasses(c || []);
    }, [schoolQuery]);

    useEffect(() => {
        if (!staffProfile) return;
        let cancelled = false;
        (async () => {
            try {
                if (isOrgAdmin) {
                    const { schools: s } = await staffGetSchools();
                    if (!cancelled) setSchools(s || []);
                }
            } catch (e) {
                if (!cancelled) setError(e && e.message ? e.message : 'Erro.');
            }
        })();
        return () => { cancelled = true; };
    }, [staffProfile, isOrgAdmin]);

    useEffect(() => {
        if (!staffProfile) return;
        let cancelled = false;
        (async () => {
            try {
                const { teachers: t } = await staffGetTeachers(teachersQueryParam);
                if (!cancelled) setTeachersList(t || []);
            } catch (e) {
                if (!cancelled) setTeachersList([]);
            }
        })();
        return () => { cancelled = true; };
    }, [staffProfile, teachersQueryParam]);

    useEffect(() => {
        if (staffProfile && staffProfile.role === 'teacher' && staffProfile.id) {
            setNewClassTeacherId(staffProfile.id);
        }
    }, [staffProfile]);

    useEffect(() => {
        if (!staffProfile) return;
        let cancelled = false;
        (async () => {
            setError(null);
            try {
                await reloadClasses();
            } catch (e) {
                if (!cancelled) setError(e && e.message ? e.message : 'Erro ao listar turmas.');
            }
        })();
        return () => { cancelled = true; };
    }, [staffProfile, reloadClasses]);

    const reloadStudents = useCallback(async (classId) => {
        if (!classId) {
            setStudents([]);
            return;
        }
        const { students: st } = await staffGetStudents(classId, schoolQuery);
        setStudents(st || []);
    }, [schoolQuery]);

    useEffect(() => {
        if (!selectedClassId) {
            setStudents([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                await reloadStudents(selectedClassId);
            } catch (e) {
                if (!cancelled) setError(e && e.message ? e.message : 'Erro ao listar alunos.');
            }
        })();
        return () => { cancelled = true; };
    }, [selectedClassId, reloadStudents]);

    useEffect(() => {
        setBulkSummary(null);
        setBulkText('');
    }, [selectedClassId]);

    const handleCreateClass = useCallback(async e => {
        e.preventDefault();
        setError(null);
        setPending(true);
        try {
            const body = {
                classCode: newCode,
                className: newName
            };
            if (isOrgAdmin) {
                if (!filterSchoolId) {
                    throw new Error('Escolha a escola para criar a turma.');
                }
                body.schoolId = filterSchoolId;
            }
            if (!newClassTeacherId) {
                throw new Error('Escolha o professor da turma.');
            }
            body.teacherId = newClassTeacherId;
            await staffCreateClass(body);
            setNewCode('');
            setNewName('');
            if (staffProfile && staffProfile.role === 'teacher' && staffProfile.id) {
                setNewClassTeacherId(staffProfile.id);
            } else {
                setNewClassTeacherId('');
            }
            await reloadClasses();
        } catch (err) {
            setError(err && err.message ? err.message : 'Erro ao criar turma.');
        } finally {
            setPending(false);
        }
    }, [isOrgAdmin, filterSchoolId, newCode, newName, newClassTeacherId, reloadClasses, staffProfile]);

    const handleAddStudent = useCallback(async e => {
        e.preventDefault();
        if (!selectedClassId) return;
        setError(null);
        setBulkSummary(null);
        setPending(true);
        try {
            await staffAddStudent(selectedClassId, newNick, schoolQuery);
            setNewNick('');
            await reloadStudents(selectedClassId);
        } catch (err) {
            setError(err && err.message ? err.message : 'Erro ao adicionar aluno.');
        } finally {
            setPending(false);
        }
    }, [selectedClassId, newNick, schoolQuery, reloadStudents]);

    const handleBulkAddStudents = useCallback(async e => {
        e.preventDefault();
        if (!selectedClassId) return;
        setError(null);
        setBulkSummary(null);
        const list = parseNicknamesBulk(bulkText);
        if (!list.length) {
            setError('Introduza pelo menos um apelido (um por linha ou separados por vírgula).');
            return;
        }
        const tooLong = list.find(n => n.length > 64);
        if (tooLong) {
            const preview = tooLong.slice(0, 48) + (tooLong.length > 48 ? '…' : '');
            setError(`Cada apelido: máx. 64 caracteres. Exemplo: «${preview}».`);
            return;
        }
        if (list.length > MAX_STUDENTS_BULK) {
            setError(`No máximo ${MAX_STUDENTS_BULK} apelidos por vez.`);
            return;
        }
        setPending(true);
        try {
            const data = await staffAddStudentsBulk(selectedClassId, list, schoolQuery);
            const created = (data.created || []).length;
            const skipped = (data.skippedDuplicates || []).length;
            const invalid = (data.invalid || []).length;
            const parts = [`${created} aluno(s) adicionado(s).`];
            if (skipped) {
                parts.push(`${skipped} ignorado(s) (já existiam nesta turma).`);
            }
            if (invalid) {
                const ex = (data.invalid || []).slice(0, 3).join(', ');
                parts.push(`${invalid} inválido(s)${ex ? `: ${ex}` : ''}.`);
            }
            setBulkSummary(parts.join(' '));
            setBulkText('');
            await reloadStudents(selectedClassId);
        } catch (err) {
            setError(err && err.message ? err.message : 'Erro ao adicionar em lote.');
        } finally {
            setPending(false);
        }
    }, [selectedClassId, bulkText, schoolQuery, reloadStudents]);

    const handleRemoveStudent = useCallback(async studentId => {
        if (!selectedClassId || !window.confirm('Remover este aluno da turma?')) return;
        setError(null);
        setBulkSummary(null);
        try {
            await staffRemoveStudent(selectedClassId, studentId, schoolQuery);
            await reloadStudents(selectedClassId);
        } catch (err) {
            setError(err && err.message ? err.message : 'Erro ao remover.');
        }
    }, [selectedClassId, schoolQuery, reloadStudents]);

    const handleClassTeacherChange = useCallback(
        async (classRow, teacherId) => {
            if (!teacherId || teacherId === classRow.teacherId) {
                return;
            }
            setError(null);
            setBusyClassId(classRow.id);
            try {
                await staffPatchClass(classRow.id, { teacherId }, schoolQuery);
                await reloadClasses();
            } catch (err) {
                setError(
                    err && err.message
                        ? err.message
                        : 'Erro ao atualizar professor da turma.'
                );
            } finally {
                setBusyClassId('');
            }
        },
        [schoolQuery, reloadClasses]
    );

    return (
        <>
            <h2>{'Gestão de turmas e alunos'}</h2>
            {isOrgAdmin ? (
                <div className={styles.toolbar}>
                    <label htmlFor="staff-class-school">{'Escola'}</label>
                    <select
                        className={styles.schoolSelect}
                        id="staff-class-school"
                        value={filterSchoolId}
                        onChange={e => setFilterSchoolId(e.target.value)}
                    >
                        <option value="">{'Selecione…'}</option>
                        {schools.map(s => (
                            <option
                                key={s.id}
                                value={s.id}
                            >
                                {s.name}
                            </option>
                        ))}
                    </select>
                </div>
            ) : null}
            {error ? (
                <p
                    className={styles.errorText}
                    role="alert"
                >
                    {error}
                </p>
            ) : null}
            <div className={styles.formCard}>
                <h3
                    className={styles.muted}
                    style={{marginTop: 0}}
                >
                    {'Nova turma'}
                </h3>
                <form onSubmit={handleCreateClass}>
                    {!isTeacher ? (
                        <p className={styles.muted}>
                            <strong>{'Professor da turma (obrigatório)'}</strong>
                        </p>
                    ) : (
                        <p className={styles.muted}>
                            {
                                'A nova turma fica sempre atribuída a si. Apenas vê e gere as suas próprias turmas.'
                            }
                        </p>
                    )}
                    {!isTeacher && teachersForNewClass.length === 0 ? (
                        <p className={styles.muted}>
                            {
                                isOrgAdmin && !filterSchoolId
                                    ? 'Escolha primeiro a escola para listar os professores desta escola.'
                                    : 'Não há professores nesta escola. Crie contas em «Professores» antes de abrir turmas.'
                            }
                        </p>
                    ) : null}
                    <div className={styles.formRow}>
                        <input
                            autoComplete="off"
                            className={styles.textInput}
                            maxLength={12}
                            placeholder="Código (ex. TURMA01)"
                            value={newCode}
                            onChange={e => setNewCode(e.target.value.toUpperCase())}
                        />
                        <input
                            className={styles.textInput}
                            placeholder="Nome da turma"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                        />
                    </div>
                    <div className={styles.formRow}>
                        {isTeacher ? (
                            <span className={styles.muted}>
                                {'Professor: '}
                                <strong>
                                    {staffProfile && staffProfile.fullName
                                        ? staffProfile.fullName
                                        : '—'}
                                </strong>
                            </span>
                        ) : (
                            <select
                                className={styles.schoolSelect}
                                disabled={
                                    pending ||
                                    (isOrgAdmin && !filterSchoolId) ||
                                    teachersForNewClass.length === 0
                                }
                                id="staff-new-class-teacher"
                                value={newClassTeacherId}
                                onChange={e => setNewClassTeacherId(e.target.value)}
                            >
                                <option value="">
                                    {'Selecione o professor…'}
                                </option>
                                {teachersForNewClass.map(t => (
                                    <option
                                        key={t.id}
                                        value={t.id}
                                    >
                                        {`${t.fullName} (${t.email})`}
                                    </option>
                                ))}
                            </select>
                        )}
                        <button
                            className={styles.btnPrimary}
                            disabled={
                                pending ||
                                (isOrgAdmin && !filterSchoolId) ||
                                (!isTeacher && teachersForNewClass.length === 0)
                            }
                            type="submit"
                        >
                            {'Criar turma'}
                        </button>
                    </div>
                </form>
            </div>
            <table className={styles.dataTable}>
                <thead>
                    <tr>
                        <th>{'Código'}</th>
                        <th>{'Nome'}</th>
                        <th>{'Professor'}</th>
                        {isOrgAdmin ? <th>{'Escola'}</th> : null}
                        <th>{'Selecionar'}</th>
                    </tr>
                </thead>
                <tbody>
                    {classes.length === 0 ? (
                        <tr>
                            <td colSpan={isOrgAdmin ? 5 : 4}>
                                {'Nenhuma turma no âmbito.'}
                            </td>
                        </tr>
                    ) : (
                        classes.map(c => {
                            const teachersForRow = teachersList.filter(
                                t => t.schoolId === c.schoolId
                            );
                            return (
                                <tr
                                    key={c.id}
                                    className={
                                        c.id === selectedClassId ? styles.rowSelected : ''
                                    }
                                >
                                    <td>{c.classCode}</td>
                                    <td>{c.className}</td>
                                    <td>
                                        {canAssignClassTeacher ? (
                                            <select
                                                className={styles.schoolSelect}
                                                disabled={
                                                    Boolean(busyClassId) ||
                                                    pending ||
                                                    teachersForRow.length === 0
                                                }
                                                value={c.teacherId || ''}
                                                onChange={e =>
                                                    handleClassTeacherChange(c, e.target.value)
                                                }
                                            >
                                                <option
                                                    disabled
                                                    value=""
                                                >
                                                    {c.teacherId
                                                        ? 'Trocar professor…'
                                                        : 'Atribuir professor…'}
                                                </option>
                                                {teachersForRow.map(t => (
                                                    <option
                                                        key={t.id}
                                                        value={t.id}
                                                    >
                                                        {`${t.fullName} (${t.email})`}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : c.teacher ? (
                                            `${c.teacher.fullName} (${c.teacher.email})`
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                    {isOrgAdmin ? <td>{c.schoolName || '—'}</td> : null}
                                    <td>
                                        <button
                                            className={styles.btnPrimary}
                                            type="button"
                                            onClick={() => setSelectedClassId(c.id)}
                                        >
                                            {'Gerir alunos'}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
            {selectedClassId ? (
                <div
                    className={styles.formCard}
                    style={{ marginTop: '1rem' }}
                >
                    <h3
                        className={styles.muted}
                        style={{marginTop: 0}}
                    >
                        {'Alunos da turma (apelidos para login)'}
                    </h3>
                    <form onSubmit={handleAddStudent}>
                        <div className={styles.formRow}>
                            <input
                                autoComplete="off"
                                className={styles.textInput}
                                maxLength={64}
                                placeholder="Apelido do aluno"
                                value={newNick}
                                onChange={e => setNewNick(e.target.value)}
                            />
                            <button
                                className={styles.btnPrimary}
                                disabled={pending}
                                type="submit"
                            >
                                {'Adicionar'}
                            </button>
                        </div>
                    </form>
                    <p className={styles.subheading}>
                        {'Adicionar em lote'}
                    </p>
                    <p className={styles.bulkHint}>
                        {
                            'Um apelido por linha. Também pode separar na mesma linha com vírgula ou ponto e vírgula. Máximo 500 por envio; até 64 caracteres por apelido.'
                        }
                    </p>
                    <form onSubmit={handleBulkAddStudents}>
                        <textarea
                            className={styles.bulkTextarea}
                            disabled={pending}
                            placeholder={'Ex.:\nAna\nBruno\nCarlos'}
                            rows={6}
                            value={bulkText}
                            onChange={e => setBulkText(e.target.value)}
                        />
                        <div className={styles.formRow}>
                            <button
                                className={styles.btnPrimary}
                                disabled={pending}
                                type="submit"
                            >
                                {'Adicionar em lote'}
                            </button>
                        </div>
                    </form>
                    {bulkSummary ? (
                        <p
                            className={styles.bulkSummary}
                            role="status"
                        >
                            {bulkSummary}
                        </p>
                    ) : null}
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>{'Apelido'}</th>
                                <th>{'Ações'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {students.map(s => (
                                <tr key={s.id}>
                                    <td>{s.nickname}</td>
                                    <td>
                                        <button
                                            className={styles.btnGhost}
                                            type="button"
                                            onClick={() => handleRemoveStudent(s.id)}
                                        >
                                            {'Remover'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </>
    );
};

StaffClassesView.propTypes = {
    staffProfile: PropTypes.object
};

StaffClassesView.defaultProps = {
    staffProfile: null
};

const StaffTeachersView = ({ staffProfile }) => {
    const isOrgAdmin = staffProfile && staffProfile.role === 'org_admin';
    const [schools, setSchools] = useState([]);
    const [filterSchoolId, setFilterSchoolId] = useState('');
    const [teachers, setTeachers] = useState([]);
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [newFullName, setNewFullName] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [formError, setFormError] = useState(null);

    const schoolQuery = isOrgAdmin && filterSchoolId ? filterSchoolId : undefined;

    const schoolNameById = useMemo(() => {
        const m = new Map();
        for (const s of schools) {
            m.set(s.id, s.name);
        }
        return m;
    }, [schools]);

    const reloadTeachers = useCallback(async () => {
        const { teachers: t } = await staffGetTeachers(schoolQuery);
        setTeachers(t || []);
    }, [schoolQuery]);

    useEffect(() => {
        if (!staffProfile) return;
        let cancelled = false;
        (async () => {
            try {
                if (isOrgAdmin) {
                    const { schools: s } = await staffGetSchools();
                    if (!cancelled) setSchools(s || []);
                }
            } catch (e) {
                if (!cancelled) setError(e && e.message ? e.message : 'Erro.');
            }
        })();
        return () => { cancelled = true; };
    }, [staffProfile, isOrgAdmin]);

    useEffect(() => {
        if (!staffProfile) return;
        let cancelled = false;
        (async () => {
            setError(null);
            try {
                await reloadTeachers();
            } catch (e) {
                if (!cancelled) {
                    setTeachers([]);
                    setError(e && e.message ? e.message : 'Erro ao listar professores.');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [staffProfile, reloadTeachers]);

    const handleCreateTeacher = useCallback(
        async e => {
            e.preventDefault();
            setFormError(null);
            const email = newEmail.trim().toLowerCase();
            const fullNm = newFullName.trim();
            const pass = newPassword;
            if (!email) {
                setFormError('Indique o email.');
                return;
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                setFormError('Email inválido.');
                return;
            }
            if (!fullNm) {
                setFormError('Indique o nome completo.');
                return;
            }
            if (!pass || pass.length < 8) {
                setFormError('Palavra-passe: mínimo 8 caracteres.');
                return;
            }
            if (isOrgAdmin && !filterSchoolId) {
                setFormError('Escolha a escola do professor.');
                return;
            }
            setPending(true);
            try {
                const body = { email, fullName: fullNm, password: pass };
                if (isOrgAdmin) {
                    body.schoolId = filterSchoolId;
                }
                await staffCreateTeacher(body);
                setNewEmail('');
                setNewFullName('');
                setNewPassword('');
                await reloadTeachers();
            } catch (err) {
                setFormError(
                    err && err.message ? err.message : 'Erro ao criar professor.'
                );
            } finally {
                setPending(false);
            }
        },
        [
            newEmail,
            newFullName,
            newPassword,
            isOrgAdmin,
            filterSchoolId,
            reloadTeachers
        ]
    );

    const showSchoolColumn = isOrgAdmin && !filterSchoolId;

    return (
        <>
            <h2>{'Professores'}</h2>
            <p className={styles.muted}>
                {
                    'Crie contas de equipa com papel Professor para a escola. O professor usa o email e palavra-passe para entrar no portal.'
                }
            </p>
            <div className={styles.toolbar}>
                {isOrgAdmin ? (
                    <>
                        <label htmlFor="staff-teach-school">{'Escola'}</label>
                        <select
                            className={styles.schoolSelect}
                            id="staff-teach-school"
                            value={filterSchoolId}
                            onChange={e => setFilterSchoolId(e.target.value)}
                        >
                            <option value="">{'Todas da rede'}</option>
                            {schools.map(s => (
                                <option
                                    key={s.id}
                                    value={s.id}
                                >
                                    {s.name}
                                </option>
                            ))}
                        </select>
                    </>
                ) : null}
            </div>
            <div className={styles.cardGrid} style={{display: 'block', marginBottom: '1rem'}}>
                <h3
                    className={styles.muted}
                    style={{marginTop: 0}}
                >
                    {'Novo professor'}
                </h3>
                <form onSubmit={handleCreateTeacher}>
                    {formError ? (
                        <p
                            className={styles.errorText}
                            role="alert"
                        >
                            {formError}
                        </p>
                    ) : null}
                    <div className={styles.formRow}>
                        <input
                            autoComplete="email"
                            className={styles.textInput}
                            id="staff-new-teacher-email"
                            maxLength={320}
                            placeholder="Email"
                            type="email"
                            value={newEmail}
                            disabled={pending}
                            onChange={e => setNewEmail(e.target.value)}
                        />
                        <input
                            autoComplete="name"
                            className={styles.textInput}
                            id="staff-new-teacher-name"
                            maxLength={255}
                            placeholder="Nome completo"
                            type="text"
                            value={newFullName}
                            disabled={pending}
                            onChange={e => setNewFullName(e.target.value)}
                        />
                    </div>
                    <div className={styles.formRow}>
                        <input
                            autoComplete="new-password"
                            className={styles.textInput}
                            id="staff-new-teacher-pass"
                            maxLength={128}
                            minLength={8}
                            placeholder="Palavra-passe (mín. 8 caracteres)"
                            type="password"
                            value={newPassword}
                            disabled={pending}
                            onChange={e => setNewPassword(e.target.value)}
                        />
                        <button
                            className={styles.btnPrimary}
                            disabled={pending}
                            type="submit"
                        >
                            {pending ? 'A criar…' : 'Criar professor'}
                        </button>
                    </div>
                </form>
            </div>
            {error ? (
                <p
                    className={styles.errorText}
                    role="alert"
                >
                    {error}
                </p>
            ) : null}
            <table className={styles.dataTable}>
                <thead>
                    <tr>
                        <th>{'Nome'}</th>
                        <th>{'Email'}</th>
                        {showSchoolColumn ? <th>{'Escola'}</th> : null}
                        <th>{'Criado em'}</th>
                    </tr>
                </thead>
                <tbody>
                    {teachers.length === 0 ? (
                        <tr>
                            <td colSpan={showSchoolColumn ? 4 : 3}>
                                {'Nenhum professor no âmbito selecionado.'}
                            </td>
                        </tr>
                    ) : (
                        teachers.map(row => (
                            <tr key={row.id}>
                                <td>
                                    {row.fullName}
                                </td>
                                <td>
                                    {row.email}
                                </td>
                                {showSchoolColumn ? (
                                    <td>
                                        {schoolNameById.get(row.schoolId) || '—'}
                                    </td>
                                ) : null}
                                <td>
                                    {row.createdAt
                                        ? new Date(row.createdAt).toLocaleString()
                                        : '—'}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </>
    );
};

StaffTeachersView.propTypes = {
    staffProfile: PropTypes.object
};

StaffTeachersView.defaultProps = {
    staffProfile: null
};

const StaffProjectsView = ({ staffProfile }) => {
    const isOrgAdmin = staffProfile && staffProfile.role === 'org_admin';
    const [schools, setSchools] = useState([]);
    const [classes, setClasses] = useState([]);
    const [filterSchoolId, setFilterSchoolId] = useState('');
    const [filterClassId, setFilterClassId] = useState('');
    const [projects, setProjects] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!staffProfile) return;
        let cancelled = false;
        (async () => {
            try {
                if (isOrgAdmin) {
                    const { schools: s } = await staffGetSchools();
                    if (!cancelled) setSchools(s || []);
                }
            } catch (e) {
                if (!cancelled) setError(e && e.message ? e.message : 'Erro.');
            }
        })();
        return () => { cancelled = true; };
    }, [staffProfile, isOrgAdmin]);

    const schoolQuery = isOrgAdmin && filterSchoolId ? filterSchoolId : undefined;

    useEffect(() => {
        setFilterClassId('');
    }, [filterSchoolId]);

    useEffect(() => {
        if (!staffProfile) return;
        let cancelled = false;
        (async () => {
            try {
                const { classes: c } = await staffGetClasses(schoolQuery);
                if (!cancelled) {
                    setClasses(c || []);
                }
            } catch (e) {
                if (!cancelled) setError(e && e.message ? e.message : 'Erro.');
            }
        })();
        return () => { cancelled = true; };
    }, [staffProfile, schoolQuery]);

    useEffect(() => {
        if (!staffProfile) return;
        let cancelled = false;
        (async () => {
            setError(null);
            try {
                const { projects: p } = await staffGetProjects({
                    schoolId: schoolQuery,
                    classId: filterClassId || undefined,
                    limit: 100
                });
                if (!cancelled) setProjects(p || []);
            } catch (e) {
                if (!cancelled) {
                    setProjects([]);
                    setError(e && e.message ? e.message : 'Erro ao listar projetos.');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [staffProfile, schoolQuery, filterClassId]);

    return (
        <>
            <h2>{'Projetos dos alunos'}</h2>
            <div className={styles.toolbar}>
                {isOrgAdmin ? (
                    <>
                        <label htmlFor="staff-proj-school">{'Escola'}</label>
                        <select
                            className={styles.schoolSelect}
                            id="staff-proj-school"
                            value={filterSchoolId}
                            onChange={e => setFilterSchoolId(e.target.value)}
                        >
                            <option value="">{'Todas'}</option>
                            {schools.map(s => (
                                <option
                                    key={s.id}
                                    value={s.id}
                                >
                                    {s.name}
                                </option>
                            ))}
                        </select>
                    </>
                ) : null}
                <label htmlFor="staff-proj-class">{'Turma'}</label>
                <select
                    className={styles.schoolSelect}
                    id="staff-proj-class"
                    value={filterClassId}
                    onChange={e => setFilterClassId(e.target.value)}
                >
                    <option value="">{'Todas no âmbito'}</option>
                    {classes.map(c => (
                        <option
                            key={c.id}
                            value={c.id}
                        >
                            {`${c.classCode} — ${c.className}`}
                        </option>
                    ))}
                </select>
            </div>
            {error ? (
                <p
                    className={styles.errorText}
                    role="alert"
                >
                    {error}
                </p>
            ) : null}
            <table className={styles.dataTable}>
                <thead>
                    <tr>
                        <th>{'Projeto'}</th>
                        <th>{'Aluno'}</th>
                        <th>{'Turma'}</th>
                        {isOrgAdmin ? <th>{'Escola'}</th> : null}
                        <th>{'Atualizado'}</th>
                    </tr>
                </thead>
                <tbody>
                    {projects.length === 0 ? (
                        <tr>
                            <td colSpan={isOrgAdmin ? 5 : 4}>
                                {'Sem projetos para os filtros atuais.'}
                            </td>
                        </tr>
                    ) : (
                        projects.map(p => (
                            <tr key={p.id}>
                                <td>{p.title}</td>
                                <td>{p.student && p.student.nickname}</td>
                                <td>
                                    {p.class && `${p.class.classCode}`}
                                </td>
                                {isOrgAdmin ? <td>{p.school && p.school.name}</td> : null}
                                <td>
                                    {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : ''}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </>
    );
};

StaffProjectsView.propTypes = {
    staffProfile: PropTypes.object
};

StaffProjectsView.defaultProps = {
    staffProfile: null
};

export {
    StaffClassesView,
    StaffDashboardView,
    StaffProjectsView,
    StaffTeachersView
};
